import { isNavigationFailure, NavigationFailureType } from 'vue-router';

/**
 * Converts an absolute URL to a relative URL by removing the scheme (http, https, etc.),
 * leading slashes, and optionally decodes the URL.
 *
 * @param {string} urlString - The URL to be converted.
 * @return {string} The relative URL or an empty string if the input is not a valid URL.
 */
const getRelativeUrl = (urlString) => {
  try {
    let newUrl = urlString.startsWith('http') ? urlString : `http://${urlString}`;
    // Attempt to create a URL object from the urlString
    newUrl = new URL(newUrl, window.location.origin); // Use the current location as the base for relative URLs
    return newUrl.pathname; // Return the pathname directly from the URL object
  } catch (e) {
    // If the URL is invalid or cannot be processed, return an empty string
    return '';
  }
};

/**
 * Checks the link URL and hostname information.
 * @method
 * @param {string} url - The URL to check.
 * @returns {UrlInfo} - Information about the link URL and hostname.
 *
 * @typedef {Object} UrlInfo
 * @property {boolean} isInternalLink - Indicates if the link is internal to the site. ce-viewer link and link with the same domain with current window are considered internal.
 * @property {boolean} isCeViewerLink - Indicates if the link is of a "ce-viewer" type, e.g., https://ce-viewer.com.
 * @property {string} currentWindowUrl - The current window URL.
 */
const analyzeUrlForNavigation = (url) => {
  const { hostname, protocol, port } = window.location;
  const currentHostWithPort = `${hostname}${port ? `:${port}` : ''}`;
  const currentWindowUrl = `${protocol}//${currentHostWithPort}`;
  const isCeViewerLink = url.includes('ce-viewer.com');
  // ?targetSite= is only read at startup, so these links must load the page, not route in-app.
  const isTargetSiteLink = /[?&]targetSite=/.test(url);

  return {
    isInternalLink:
      !isTargetSiteLink &&
      (isCeViewerLink || url.startsWith(currentHostWithPort) || url.startsWith(currentWindowUrl)),
    isCeViewerLink,
    currentWindowUrl
  };
};

const handleNavigationFailures = (failure) => {
  // https://router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures
  // In v3.2.0, Navigation Failures were exposed through the two optional callbacks of
  // router.push: onComplete and onAbort. Since version 3.1.0, router.push and router.replace
  // return a Promise if no onComplete/onAbort callback is provided. This Promise resolves
  // instead of invoking onComplete and rejects instead of invoking onAbort.

  // if you are using router.push or router.replace, you might come across an
  // "Uncaught (in promise) Error" message followed by a more specific message in your console

  // Other reference: https://stackoverflow.com/questions/62462276/how-to-solve-avoided-redundant-navigation-to-current-location-error-in-vue

  // TODO: find a better solution to this
  if (isNavigationFailure(failure, NavigationFailureType.redirected)) {
    console.warn('Your Policy Connect request have been redirected');
  }

  // do nothing on (or basically allow) cancelled, aborted, and duplicated types
};

export default { handleNavigationFailures, analyzeUrlForNavigation, getRelativeUrl };
